import { TimezoneModel } from "generated/api";

import { getTimezoneOffsetInMinutes } from "./timezoneUtils";

const NON_PRIORITISED_ORDER = 1000;
const OFFSET_TIMEZONE_ID_PATTERN = /^([+-])(\d{2}):(\d{2})$/;
const UNAVAILABLE_UTC_REFERENCE = "UTC reference unavailable";

const timezoneOffsetsCache = new Map<string, number[] | null>();

export interface TimezoneDisplayOption {
  id: string;
  territory: string;
  description: string;
  utcReference: string;
  isPrioritised: boolean;
  order: number;
  sortOffset: number;
  searchText: string;
  originalIndex: number;
}

interface BuildTimezoneOptionsConfig {
  referenceYear?: number;
  selectedTimezoneId?: string | null;
  selectedTimezoneDescription?: string | null;
}

export const parseOffsetTimezoneId = (timezoneId: string | null | undefined) => {
  const match = timezoneId?.match(OFFSET_TIMEZONE_ID_PATTERN);

  if (!match) {
    return null;
  }

  const [, sign, hours, minutes] = match;
  const absoluteOffset = Number(hours) * 60 + Number(minutes);

  return sign === "-" ? -absoluteOffset : absoluteOffset;
};

export const formatUtcOffset = (offsetInMinutes: number) => {
  const sign = offsetInMinutes < 0 ? "-" : "+";
  const absoluteOffset = Math.abs(offsetInMinutes);
  const hours = Math.floor(absoluteOffset / 60);
  const minutes = absoluteOffset % 60;

  return `UTC${sign}${hours}${minutes ? `:${minutes.toString().padStart(2, "0")}` : ""}`;
};

export const getTimezoneReferenceOffsets = (timezoneId: string, referenceYear = new Date().getUTCFullYear()) => {
  const offsetTimezoneId = parseOffsetTimezoneId(timezoneId);

  if (offsetTimezoneId !== null) {
    return [offsetTimezoneId];
  }

  const cacheKey = `${timezoneId}:${referenceYear}`;

  if (timezoneOffsetsCache.has(cacheKey)) {
    return timezoneOffsetsCache.get(cacheKey) ?? null;
  }

  const offsets = Array.from({ length: 12 }, (_, month) =>
    getTimezoneOffsetInMinutes(timezoneId, new Date(Date.UTC(referenceYear, month, 15, 12, 0, 0))),
  ).filter((offset): offset is number => offset !== null);

  const uniqueOffsets = Array.from(new Set(offsets)).sort((left, right) => left - right);
  const result = uniqueOffsets.length ? uniqueOffsets : null;

  timezoneOffsetsCache.set(cacheKey, result);

  return result;
};

export const formatTimezoneUtcReference = (timezoneId: string, referenceYear = new Date().getUTCFullYear()) => {
  const offsets = getTimezoneReferenceOffsets(timezoneId, referenceYear);

  if (!offsets) {
    return UNAVAILABLE_UTC_REFERENCE;
  }

  const reference = offsets.map(formatUtcOffset).join(" / ");

  return offsets.length > 1 ? `${reference} (DST)` : reference;
};

export const buildTimezoneDisplayText = (
  timezoneId: string | null | undefined,
  referenceYear = new Date().getUTCFullYear(),
) => {
  if (!timezoneId) {
    return "";
  }

  return `${timezoneId} | ${formatTimezoneUtcReference(timezoneId, referenceYear)}`;
};

const toTimezoneDisplayOption = (
  timezone: TimezoneModel,
  originalIndex: number,
  referenceYear: number,
): TimezoneDisplayOption | null => {
  if (!timezone.id) {
    return null;
  }

  const order = timezone.order ?? NON_PRIORITISED_ORDER;
  const offsets = getTimezoneReferenceOffsets(timezone.id, referenceYear);
  const utcReference = offsets ? formatTimezoneUtcReference(timezone.id, referenceYear) : UNAVAILABLE_UTC_REFERENCE;
  const sortOffset = offsets?.[0] ?? Number.POSITIVE_INFINITY;
  const description = timezone.description ?? timezone.id;
  const isPrioritised = order < NON_PRIORITISED_ORDER;

  return {
    id: timezone.id,
    territory: timezone.id,
    description,
    utcReference,
    isPrioritised,
    order,
    sortOffset,
    searchText: `${timezone.id} ${utcReference} ${description}`,
    originalIndex,
  };
};

const compareTimezoneOptions = (left: TimezoneDisplayOption, right: TimezoneDisplayOption) => {
  if (left.isPrioritised || right.isPrioritised) {
    if (left.isPrioritised && right.isPrioritised) {
      return left.order - right.order || left.originalIndex - right.originalIndex;
    }

    return left.isPrioritised ? -1 : 1;
  }

  return left.sortOffset - right.sortOffset || left.territory.localeCompare(right.territory);
};

export const buildTimezoneOptions = (
  timezones: TimezoneModel[] | null | undefined,
  {
    referenceYear = new Date().getUTCFullYear(),
    selectedTimezoneId,
    selectedTimezoneDescription,
  }: BuildTimezoneOptionsConfig = {},
) => {
  const optionsById = new Map<string, TimezoneDisplayOption>();

  (timezones ?? []).forEach((timezone, index) => {
    const option = toTimezoneDisplayOption(timezone, index, referenceYear);

    if (option && !optionsById.has(option.id)) {
      optionsById.set(option.id, option);
    }
  });

  if (selectedTimezoneId && !optionsById.has(selectedTimezoneId)) {
    const selectedOption = toTimezoneDisplayOption(
      {
        id: selectedTimezoneId,
        description: selectedTimezoneDescription ?? selectedTimezoneId,
        order: NON_PRIORITISED_ORDER,
      },
      optionsById.size,
      referenceYear,
    );

    if (selectedOption) {
      optionsById.set(selectedOption.id, selectedOption);
    }
  }

  return Array.from(optionsById.values()).sort(compareTimezoneOptions);
};
